<template>
  <div class="pb-2">

    <div class="flex flex-row">
      <div class="flex-grow flex-1 pb-2 flex items-center">
        <gd-dropdown
          v-model="boxShadow"
          :options="dropdownOptions(properties.boxShadow)"
          variant="dark"
          size="full"
        />
      </div>
    </div>

    <div v-if="isCustom">
      <div class="w-full h-8 my-2 flex items-center justify-between">
        <div class="text-xs font-semibold text-secondary-invert mb-1">{{ $t('frontend_builder_text_section_x_offset') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('box', 'x'),
              'bg-primary text-primary-invert': isSelected('box', 'x'),
          }"
          @click="select('box', 'x')"
        >
          {{ getShadowValue('box', 'x') }}
        </div>

        <div class="text-xs font-semibold text-secondary-invert mb-1">{{ $t('frontend_builder_text_section_y_offset') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('box', 'y'),
              'bg-primary text-primary-invert': isSelected('box', 'y'),
          }"
          @click="select('box', 'y')"
        >
          {{ getShadowValue('box', 'y') }}
        </div>
      </div>

      <div class="w-full h-8 my-2 flex items-center justify-between">
        <div class="text-xs font-semibold text-secondary-invert mb-1 pr-5">{{ $t('frontend_builder_text_section_blur') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('box', 'blur'),
              'bg-primary text-primary-invert': isSelected('box', 'blur'),
          }"
          @click="select('box', 'blur')"
        >
          {{ getShadowValue('box', 'blur') }}
        </div>
        <div class="text-xs font-semibold text-secondary-invert mb-1">{{ $t('frontend_builder_text_section_spread') }}</div>
        <div
          class="w-12 h-8 flex items-center justify-center text-center text-sm rounded border-solid border-secondary-light cursor-pointer select-none focus:outline-none"
          :class="{
              'border bg-secondary text-secondary-invert': !isSelected('box', 'spread'),
              'bg-primary text-primary-invert': isSelected('box', 'spread'),
          }"
          @click="select('box', 'spread')"
        >
          {{ getShadowValue('box', 'spread') }}
        </div>
      </div>

      <div class="w-full h-8 my-2 flex">
        <div class="w-6/12 flex pr-2">
          <gd-button
            class="mb-1"
            v-for="(set, position) in sets"
            :key="set"
            size="sm"
            :variant="box.set === set ? 'primary' : 'dark'"
            :group-position="position === 0 ? 'left' : 'right'"
            @click="selectSet(set)"
          >
            {{set}}
          </gd-button>
        </div>

        <div class="w-6/12 flex items-center">
          <div class="text-xs font-semibold text-secondary-invert mb-1 px-2">{{ $t('frontend_color') }}</div>
          <color-picker
            v-model="boxColor"
            :colors="properties.color"
          ></color-picker>
        </div>
      </div>

      <div v-if="type==='box' && selected" class="my-2">
        <gd-slider
          v-model="slider"
          :min="min"
          :max="max"
          :readonly="false"
        />
      </div>
    </div>

  </div>

</template>

<script>
import GdSlider from '@groovepages/gd-ui-kit/components/Slider';
import GdButton from '@groovepages/gd-ui-kit/components/Button';
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker'
import tailwindConfig from '@groovepages/gp-page-ui-kit/tailwind';

export default {
  components: { GdSlider, GdButton, ColorPicker },
  props: {
    styles: {
      required: true,
    },
  },
  data() {
    return {
      properties: {
        boxShadow: {
          None: 'none',
          Custom: 'custom',
          Default: 'rgba(0, 0, 0, 0.1) 0px 2px 4px 0px',
          Md:
            'rgba(0, 0, 0, 0.12) 0px 4px 8px 0px, rgba(0, 0, 0, 0.08) 0px 2px 4px 0px',
          Lg:
            'rgba(0, 0, 0, 0.11) 0px 15px 30px 0px, rgba(0, 0, 0, 0.08) 0px 5px 15px 0px',
          Xl: 'rgba(0, 0, 0, 0.21) 0px 2px 10px 3px',
          Inner: 'rgba(0, 0, 0, 0.06) 0px 2px 4px 0px inset',
          Emboss: 'rgba(0, 0, 0, 0.2) 0px -3px 0px inset'
        },
        color: tailwindConfig.colors,
      },
      min: 0,
      max: 30,
      type: 'box',
      selected: '',
      sets: ['inset', 'outset'],
      box: {
        x: 0,
        y: 0,
        blur: 0,
        spread: 0,
        set: 'outset',
        color: 'rgba(0, 0, 0, 0.1)'
      },

    };
  },
  computed: {
    isCustom() {
      return !Object.values(this.properties.boxShadow).some(i => i == this.styles)
    },
    boxColor: {
      get() {
        return this.box.color;
      },
      set(value) {
        this.box.color = value;
        this.setProperty('box');
      }
    },
    slider: {
      get() {
        return parseInt(this[this.type][this.selected]);
      },
      set(value) {
        this[this.type][this.selected] = `${value}px`;
        this.setProperty(this.type);
      }
    },
    boxShadow: {
      get() {
        if (this.isCustom) {
          this.getProperty('box', this.styles);
          return 'custom';
        }
        return this.styles;
      },
      set(value) {
        if(value === 'custom') {
          this.setProperty('box');
          return;
        }
        this.$emit('change', value);
      }
    },
  },
  methods: {
    dropdownOptions(property) {
      return Object.keys(property).map(text => {
        return { text, value: property[text] };
      });
    },
    select(type, value) {
      this.type = type;
      this.selected = value;
    },
    selectSet(value) {
      this.box.set = value;
      this.setProperty('box');
    },
    isSelected(type, value) {
      if(this.type === type && this.selected === value) {
        return true;
      }
      return false;
    },
    getShadowValue(type, value) {
      return this[type][value];
    },
    getProperty(type , value) {
      if(!value) return;

      const set = value.indexOf('inset') !== -1 ? 'inset' : 'outset';
      const rgba = value.substring(value.indexOf('rgba('), value.indexOf(')')+1);

      value = value.replace(set, '');
      value = value.replace(rgba, '');
      value = value.trim();
      value = value.replace('  ', ' ');
      value = value.split(' ');

      if(type ==='box' && value) {
        this.box.set = set;
        this.box.color = rgba;
        this.box.x = value[0];
        this.box.y =  value[1];
        this.box.blur = value[2];
        this.box.spread = value[3];
      }
    },
    setProperty(type) {
      if(type ==='box') {
        const { x, y, blur, spread, color, set } = this.box;
        this.$emit('change', `${color} ${x} ${y} ${blur} ${spread} ${set === 'inset' ? 'inset' : ''}`);
      }
    }
  }
};
</script>
